import Constants, { AMP_TYPES } from './constants';
import Utils from './utils';
import {
  OPEN_LOOP_GAIN,
  RESISTOR_TOLERANCE,
  CMRR_OFFSET,
  INPUT_OFFSET_CURRENT,
  INPUT_OFFSET_VOLTAGE,
  INPUT_OFFSET_VOLTAGE_DRIFT,
  PSRR_DC,
  BIAS_CURRENT,
  RESISTOR_TEMPCO,
} from './formulas';
import { htmlSafe } from '@ember/template';
import Big from 'big.js';

const PARAM = Constants.CDP.PARAMETERS;

const DEFAULT_USE_IN_CHART = { gain: false, offset: false, byPart: true };

/**
 * Checks if a set of parameters are not null and numeric. (Don't use on strings.)
 * @param  {...any} args List of numeric parameters
 */
export function notNull(...args) {
  for (const arg of args) {
    // for (let i = 0; i < args.length; i++) {
    if (arg == null) return false;
    if (isNaN(arg)) return false;
  }
  return true;
}

/**
 * Get an unformatted parameter value from an object. Failure to retrieve it returns `null`.
 * @param {*} obj CDP data style object with parameter IDs as keys
 * @param {*} key Parameter to search for and extract value from, if available
 */
function tryParse(obj, key) {
  if (!obj) return null;
  const value = obj[key];
  if (typeof value === 'undefined' || typeof value.value === 'undefined') return null;
  if (value.value[0] === null) return null;
  const parsed = value.value[0];
  // if(parsed === null) return null;
  return parsed;
}

/**
 * Get an float value from an object. Failure to retrieve it returns `null`.
 * @param {*} obj CDP data style object with parameter IDs as keys
 * @param {*} key Parameter to search for and extract value from, if available
 */
export function tryParseFloat(obj, key) {
  const value = tryParse(obj, key);
  if (value == null) return null;
  const parsed = parseFloat(value);
  if (isNaN(parsed)) return null;
  return parsed;
}

/**
 * Get a string value from an object. Failure to retrieve it returns `null`.
 * @param {*} obj CDP data style object with parameter IDs as keys
 * @param {*} key Parameter to search for and extract value from, if available
 */
export function tryParseString(obj, key) {
  const value = tryParse(obj, key);
  return value;
}

/**
 * Find a specific type of part within the signal chain variable
 * @param {*} obj CDP data style object with parameter IDs as keys
 * @param {*} key Parameter to search for and extract value from, if available
 */
export function findInSignalChain(currentSignalChain, id) {
  const found = currentSignalChain.find((t) => t.config && t.config.id == id) || {};
  return found.selected; // null if not found
}

/**
 * Creates a function to add details to a specific error category
 * (See comments within this for more information)
 *
 * @param {*} category An empty list to add error details to.
 */
export function addDetailsForErrorCategory(category, model) {
  /**
   * Add details to a specific error category
   *
   * @param errorTranslation Use this to get localized display text
   * @param validationCheck Either a list of parameters to run notNull against, or true/false for valid/invalid
   * @param value The output display value (if error, use 0)
   * @param errorMessage If error, this message will be displayed in popup
   *
   */
  return function (
    errorTranslation,
    validationCheck,
    value,
    errorMessage = '',
    showHelpTopic = false,
    useInChart = {},
  ) {
    let valid = false,
      nameFn = (intl) => htmlSafe(intl.t(`error_details_message.${errorTranslation}.title`));

    const combinedUseInChart = Object.assign({}, DEFAULT_USE_IN_CHART, useInChart || {}),
      id = errorTranslation.replaceAll('_', '-');

    if (Array.isArray(validationCheck)) {
      if (validationCheck.length > 0 && Array.isArray(validationCheck[0])) {
        // Hybrid method: Show errors based on list of lists...
        // Anything that triggers "false" is grounds for failure. valid = 'true'.

        const invalidItem = validationCheck.find((item) => !item[0]);
        if (invalidItem) errorMessage = invalidItem[1];
        else valid = true;
      } else {
        // List method of validation: Check each of the included items to make sure they aren't null
        valid = notNull(...validationCheck);
      }
    } else {
      // Binary method of validation: Use the calculated result
      valid = validationCheck;
    }

    if (valid) {
      category.push({
        id,
        nameFn,
        value: Math.abs(value),
        errorTranslation,
        showHelpTopic,
        useInChart: combinedUseInChart,
      }); // error value always >= 0
    } else {
      let errorValue = 0,
        foundErrorData = false;

      if (id in model.signalChainHandler.errorData) {
        errorValue = model.signalChainHandler.errorData[id];
        foundErrorData = errorValue > 0;
      }

      category.push({
        id,
        nameFn,
        value: errorValue,
        foundErrorData,
        errorTranslation,
        showHelpTopic,
        errorMessage,
        useInChart: combinedUseInChart,
      });
    }
  };
}

export function addChartFn(chartInfo) {
  return function (name, className, details) {
    if (!!details && details.length > 0) chartInfo.push({ name, className, details });
  };
}

export function getMaxTempRange(model) {
  const maxWorkingTemp = parseInt(model.signalChainHandler.dac.data.operatingTemperature.min);
  const minWorkingTemp = parseInt(model.signalChainHandler.dac.data.operatingTemperature.max);

  if (model.signalChainHandler.dac.data.calibrationToggled) return Math.abs(maxWorkingTemp - minWorkingTemp) / 2;
  else return Math.max(Math.abs(maxWorkingTemp - 25), Math.abs(minWorkingTemp - 25));
}

// Requires intl service to work correctly.
export function notAvailable(msg) {
  if (msg && msg.length > 0) {
    return msg;
  }
  return "'NotAvailable' not defined yet";
}
export function setNotAvailableMsg(_notAvailable) {
  notAvailable(_notAvailable);
}

export function getVoltRefErrors(voltRef, dacSelected, model) {
  let voltRefErrors = [];
  if (!voltRef) return voltRefErrors;

  let addVRDetails = addDetailsForErrorCategory(voltRefErrors, model);

  const maxTempRange = getMaxTempRange(model),
    // input variables
    vOut = tryParseFloat(voltRef, PARAM.VOUT),
    initialAccuracy = tryParseFloat(voltRef, PARAM.INITIAL_ACCURACY),
    tempCoefficient = tryParseFloat(voltRef, PARAM.VOUT_TEMPCO_MAX),
    loadRegulation = tryParseFloat(voltRef, PARAM.LOAD_REGULATION),
    refPowerSupplyRipple = model.signalChainHandler.dac.data.powerSupplyRipple, // Comes from user input, Application Parameters tab
    lineRegulation = tryParseFloat(voltRef, PARAM.LINE_REGULATION),
    topology = model.topology;

  let dacInputResistance = tryParseFloat(dacSelected, PARAM.INPUT_RESISTANCE); // ohms. Param is missing, looks like 5464

  if (topology.hasInvertingAmp) {
    const r1 = model.resistorHandler.currentInvertingResistor.values.res1;
    if (
      !topology.hasNonInvertingInput ||
      (topology.hasNonInvertingInput && (topology.hasNonInvertingAmp || topology.hasNonInvertingBuffer))
    ) {
      dacInputResistance = r1;
    } else {
      dacInputResistance = Utils.isNumeric(dacInputResistance)
        ? (r1 * dacInputResistance) / (r1 + dacInputResistance)
        : null;
    }
  } else if (topology.hasBuffer || topology.hasNonInvertingAmp || topology.hasNonInvertingBuffer) {
    dacInputResistance = 100000000;
  }

  const outCurrentMax =
    Utils.isNumeric(vOut) && Utils.isNumeric(dacInputResistance) ? (vOut / dacInputResistance) * 1000 : null;

  // Error calculations
  addVRDetails(
    'initial',
    [initialAccuracy],
    initialAccuracy * 10000,
    notAvailable(['initial_accuracy']),
    true, // show help topic
  );

  addVRDetails(
    'tempco',
    [tempCoefficient, maxTempRange],
    tempCoefficient * maxTempRange,
    notAvailable(['temp_coefficient', 'max_temp']),
    true, // show help topic
    { gain: true },
  );

  addVRDetails(
    'load_reg',
    [loadRegulation, outCurrentMax],
    loadRegulation * outCurrentMax,
    notAvailable(['load_reg', 'out_curr_max']),
    true, // show help topic
  );

  addVRDetails(
    'line_reg',
    [refPowerSupplyRipple, lineRegulation],
    refPowerSupplyRipple * lineRegulation,
    notAvailable(['pwr_supply_ripple', 'line_reg']),
    true, // show help topic
  );

  // addVRDetails("out_noise", [vNoise, refOutVoltage],
  //     vNoise / refOutVoltage * Math.pow(10, 6),
  //     notAvailable(['vnoise', 'ref_out_voltage']),
  //     true // show help topic
  // )

  addVRDetails('long_term_drift', [], 0, 'Not implemented', true, { byPart: false, gain: true });

  return voltRefErrors;
}

function validateAmpType(ampType, type) {
  // Make sure new amp typeis used. Needs to be part of the AMP_TYPES constant or something similar
  if (!Object.values(AMP_TYPES).includes(ampType)) {
    console.error(`Unknown Amp Type for ${type}.`);
  }
}

// Can be used to get errors for inverting amp, non-inverting amp, and buffer
export function getInvertingAmpErrors(
  ampType = -1,
  invertingAmp,
  inputVoltage,
  dacSelected,
  model,
  resistor,
  // extraOptions = {}
) {
  validateAmpType(ampType, 'Inverting Amp');

  let invertingAmpErrors = [],
    addIADetails = addDetailsForErrorCategory(invertingAmpErrors, model);

  const maxTempRange = getMaxTempRange(model),
    refPowerSupplyRipple = model.signalChainHandler.dac.data.powerSupplyRipple; // Comes from user input, Application Parameters tab

  let r1, r2, resistorTolerance, resistorTempco, hasInternalResistors;
  if (!!resistor && !!resistor.values) {
    r1 = resistor.values.res1;
    r2 = resistor.values.res2;
    resistorTolerance = resistor.values.tolerance;
    resistorTempco = resistor.values.tempco;
    hasInternalResistors = resistor.hasInternalResistors;
  }

  // Intermediate variables (topology can be 'buffered', 'inverting', 'non-inverting')
  let idealGain = 1;
  if (ampType == AMP_TYPES.INVERTING)
    // == "Unipolar, Vout = 0 to +VREF"
    idealGain = (-1 * r2) / r1;
  if (ampType == AMP_TYPES.NON_INVERTING)
    // == "Unipolar, Vout = 0 to -VREF, Unbuffered"
    idealGain = r2 / r1 + 1;
  const offsetGain = ampType == AMP_TYPES.INVERTING || ampType == AMP_TYPES.NON_INVERTING ? r2 / r1 + 1 : 1; // Buffers have offset gain of 1

  // "for" inverting amp but used in other calculations
  const invertingAmpOutputVoltage = idealGain * inputVoltage,
    resistorMatching = tryParseFloat(dacSelected, PARAM.INVERTING_RESISTORS.TOLERANCE), // Inputs
    vos = tryParseFloat(invertingAmp, PARAM.VOS), // Desired: VoS MAX
    aVo = tryParseFloat(invertingAmp, PARAM.AVO), // "Aol",	Open Loop Voltage Gain
    cmrrMin = tryParseFloat(invertingAmp, PARAM.CMRR_MIN),
    psrrDCMin = tryParseFloat(invertingAmp, PARAM.PSRR_DC_MIN),
    inputOffsetCurrent = tryParseFloat(invertingAmp, PARAM.INPUT_OFFSET_CURRENT_MAX), // 5030 is max
    inputOffsetDrift = tryParseFloat(invertingAmp, PARAM.INPUT_OFFSET_DRIFT),
    biasInputCurrent = tryParseFloat(invertingAmp, PARAM.BIAS_INPUT_CURRENT); // Ibias (max) in A

  // Errors
  // Input Offset Voltage Error
  addIADetails(
    'io_voltage',
    [vos, invertingAmpOutputVoltage],
    INPUT_OFFSET_VOLTAGE(vos, offsetGain, invertingAmpOutputVoltage),
    notAvailable(['vos', 'inv_amp_out_v']),
    true, // show help topic
  );

  // Input Offset Voltage Drift Error
  addIADetails(
    'io_voltage_drift',
    [inputOffsetDrift, invertingAmpOutputVoltage],
    INPUT_OFFSET_VOLTAGE_DRIFT(inputOffsetDrift, maxTempRange, offsetGain, invertingAmpOutputVoltage),
    notAvailable(['iodrift', 'inv_amp_out_v']),
    true, // show help topic
    { gain: true },
  );

  // Bias Current Error
  if (ampType == AMP_TYPES.INVERTING || ampType == AMP_TYPES.NON_INVERTING) {
    addIADetails(
      'bias_current',
      [biasInputCurrent, invertingAmpOutputVoltage],
      BIAS_CURRENT(biasInputCurrent, offsetGain, invertingAmpOutputVoltage, r1, r2),
      notAvailable(['bias_in_current', 'output_voltage']),
      true, // show help topic
    );
  }

  // Input Offset Current Error Inverting
  if (ampType == AMP_TYPES.INVERTING || ampType == AMP_TYPES.NON_INVERTING) {
    addIADetails(
      'io_current',
      [inputOffsetCurrent, invertingAmpOutputVoltage],
      INPUT_OFFSET_CURRENT(inputOffsetCurrent, offsetGain, invertingAmpOutputVoltage, r1, r2),
      notAvailable(['io_current', 'inv_amp_out_v']),
      true, // show help topic
    );
  }

  addIADetails(
    'psrr_offset',
    [psrrDCMin],
    PSRR_DC(refPowerSupplyRipple, psrrDCMin, offsetGain, invertingAmpOutputVoltage),
    notAvailable(['psrr_dc']),
    true, // show help topic
  );

  // CMRR Offset Error
  if (ampType === AMP_TYPES.INVERTING) addIADetails('cmrr_offset', [], 0, '', true);
  else {
    addIADetails(
      'cmrr_offset',
      [inputVoltage, cmrrMin, offsetGain, invertingAmpOutputVoltage],
      CMRR_OFFSET(inputVoltage, cmrrMin, offsetGain, invertingAmpOutputVoltage),
      notAvailable(['inv_amp_out_v', 'cmrr_min', 'ideal_gain', 'inv_amp_out_v']),
      true, // show help topic
    );
  }

  // Open Loop Gain Error
  addIADetails(
    'open_loop_gain',
    [aVo, idealGain],
    OPEN_LOOP_GAIN(aVo, idealGain, r1, r2, ampType),
    notAvailable(['avo', 'ideal_gain']),
    true, // show help topic
  );

  // Resistor Tolerance Error
  if (ampType !== AMP_TYPES.BUFFER) {
    let errorMessage, checks;

    if (hasInternalResistors) {
      checks = [resistorMatching, r1, r2];
      errorMessage = notAvailable(['res_matching', 'r1', 'r2']);
    } else {
      // original
      errorMessage = notAvailable(['res_tolerance', 'r1', 'r2']);
      checks = [resistorTolerance, r1, r2];
    }

    addIADetails(
      'resistor_tolerance',
      checks,
      RESISTOR_TOLERANCE(
        hasInternalResistors,
        resistorTolerance,
        resistorMatching,
        r1,
        r2,
        idealGain,
        ampType === AMP_TYPES.NON_INVERTING,
      ),
      errorMessage,
      true, // show help topic'
    );

    addIADetails(
      'resistor_tempco',
      [resistorTempco, r1, r2, maxTempRange],
      RESISTOR_TEMPCO(resistorTempco, r1, r2, maxTempRange, ampType === AMP_TYPES.NON_INVERTING),
      notAvailable(['res_tempco', 'r1', 'r2', 'op_temp']),
      true, // show help topic
      { gain: true },
    );
  }

  return { errors: invertingAmpErrors, outputVoltage: invertingAmpOutputVoltage };
}

export function getOutAmpErrors(
  ampType,
  outAmp,
  dacSelected,
  model,
  resistor,
  inputVoltage,
  showToleranceAndTempCoErrors = false,
  hasInternalResistors = false,
) {
  validateAmpType(ampType, 'Output Amp');

  const r1 = resistor.res1 || Constants.DEFAULTS.OUTPUT_RESISTOR_VALUE,
    r2 = resistor.res2 || Constants.DEFAULTS.OUTPUT_RESISTOR_VALUE,
    resistorTolerance = resistor.tolerance,
    resistorTempco = resistor.tempco,
    isTopologyInverting = ampType === AMP_TYPES.INVERTING,
    isTopologyNonInverting = ampType === AMP_TYPES.NON_INVERTING;

  let outAmpErrors = [],
    addOADetails = addDetailsForErrorCategory(outAmpErrors, model);

  // from this
  const maxTempRange = getMaxTempRange(model),
    vos = tryParseFloat(outAmp, PARAM.VOS),
    inputOffsetDrift = tryParseFloat(outAmp, PARAM.INPUT_OFFSET_DRIFT); // Vos TC (V/°C)... Check analog.com/en/parametricsearch or pic.cldnet.analog.com for 11070
  // user input
  // let invertingAmpInputVoltage = tryParseFloat(voltRef, 4995); // Getting from Voltage Reference on purpose.

  // This is Input Voltage (max).
  const biasInputCurrent = tryParseFloat(outAmp, PARAM.BIAS_INPUT_CURRENT), // Ibias (max) in A
    cmrrMin = tryParseFloat(outAmp, PARAM.CMRR_MIN),
    aVo = tryParseFloat(outAmp, PARAM.AVO), // A VO (dB)
    inputOffsetCurrent = tryParseFloat(outAmp, PARAM.INPUT_OFFSET_CURRENT_MAX), // this is typ; 5030 is max
    psrrDCMin = tryParseFloat(outAmp, PARAM.PSRR_DC_MIN),
    refPowerSupplyRipple = model.signalChainHandler.dac.data.powerSupplyRipple; // Comes from user input, Application Parameters tab

  // Intermediate variables for output amp (topology can be 'buffered', 'inverting', 'non-inverting')
  let idealGain = 1;
  if (isTopologyInverting)
    // == "Unipolar, Vout = 0 to +VREF"
    idealGain = (-1 * r2) / r1;
  if (isTopologyNonInverting)
    // == "Unipolar, Vout = 0 to -VREF, Unbuffered"
    idealGain = r2 / r1 + 1;

  const offsetGain = isTopologyInverting || isTopologyNonInverting ? r2 / r1 + 1 : 1;

  let idealOutputVoltage = null; // to output later

  const outputVoltage = (idealOutputVoltage = inputVoltage != null ? idealGain * inputVoltage : null);

  // errors
  addOADetails(
    'io_voltage',
    [vos, outputVoltage],
    INPUT_OFFSET_VOLTAGE(vos, offsetGain, outputVoltage),
    notAvailable(['vos', 'output_voltage']),
    true, // show help topic
  );

  addOADetails(
    'io_voltage_drift',
    [inputOffsetDrift, outputVoltage],
    INPUT_OFFSET_VOLTAGE_DRIFT(inputOffsetDrift, maxTempRange, idealGain, outputVoltage),
    notAvailable(['iodrift', 'output_voltage']),
    true, // show help topic
    { offset: true },
  );

  // Buffered type does not have these
  if (ampType !== AMP_TYPES.BUFFER) {
    // Bias Current Error
    addOADetails(
      'bias_current',
      [biasInputCurrent, r1, r2, outputVoltage],
      BIAS_CURRENT(biasInputCurrent, offsetGain, outputVoltage, r1, r2),
      notAvailable(['bias_in_current', 'r2', 'output_voltage']),
      true, // show help topic
    );

    addOADetails(
      'io_current',
      [inputOffsetCurrent, outputVoltage, r1, r2],
      INPUT_OFFSET_CURRENT(inputOffsetCurrent, offsetGain, outputVoltage, r1, r2),
      notAvailable(['io_current', 'output_voltage', 'r1', 'r2']),
      true, // show help topic
    );
  }

  addOADetails(
    'psrr_offset',
    [psrrDCMin],
    PSRR_DC(refPowerSupplyRipple, psrrDCMin, offsetGain, outputVoltage),
    notAvailable(['psrr_dc']),
    true, // show help topic
  );

  if (ampType === AMP_TYPES.INVERTING) addOADetails('cmrr_offset', [], 0, '', true);
  else {
    addOADetails(
      'cmrr_offset',
      [inputVoltage, cmrrMin, outputVoltage],
      CMRR_OFFSET(inputVoltage, cmrrMin, offsetGain, outputVoltage),
      notAvailable(['input_v', 'cmrr_min', 'output_voltage']),
      true, // show help topic
    );
  }

  addOADetails(
    'open_loop_gain',
    [aVo, idealGain],
    OPEN_LOOP_GAIN(aVo, idealGain, r1, r2, ampType),
    notAvailable(['avo', 'ideal_gain']),
    true, // show help topic
  );

  if (showToleranceAndTempCoErrors) {
    const resistorMatching = tryParseFloat(dacSelected, PARAM.INVERTING_RESISTORS.TOLERANCE);

    // Tolerance error
    {
      let errorMessage, checks;
      if (hasInternalResistors) {
        checks = [resistorMatching, r1, r2];
        errorMessage = notAvailable(['res_matching', 'r1', 'r2']);
      } else {
        // original
        errorMessage = notAvailable(['res_tolerance', 'r1', 'r2']);
        checks = [resistorTolerance, r1, r2];
      }

      addOADetails(
        'resistor_tolerance',
        checks,
        RESISTOR_TOLERANCE(
          hasInternalResistors,
          resistorTolerance,
          resistorMatching,
          r1,
          r2,
          idealGain,
          ampType === AMP_TYPES.NON_INVERTING,
        ),
        errorMessage,
        true, // show help topic
      );
    }

    addOADetails(
      'resistor_tempco',
      [resistorTempco, r1, r2, maxTempRange],
      RESISTOR_TEMPCO(resistorTempco, r1, r2, maxTempRange, ampType === AMP_TYPES.NON_INVERTING),
      notAvailable(['res_tempco', 'r1', 'r2', 'op_temp']),
      true, // show help topic
      { gain: true },
    );
  }

  return { outAmpErrors, idealOutputVoltage };
}

export function getTotalErrorInfo(chartInfo, ideal, model) {
  let totalErrorInfo;

  let sumBigDetails = (details) => {
    return details.reduce((detailTotal, detailCurrent) => {
      if (isNaN(detailCurrent.value) || !isFinite(detailCurrent.value)) {
        console.warn('Invalid value for detail ', detailCurrent, ', setting to 0');
        detailCurrent.value = 0;
      }
      return detailTotal.add(detailCurrent.value);
    }, new Big(0));
  };

  let sumBigDetailsSquared = (details) => {
    return details.reduce(
      (detailTotal, detailCurrent) => detailTotal.add(new Big(detailCurrent.value).pow(2)),
      new Big(0),
    );
  };

  if (model.signalChainHandler.dac.data.calibrationToggled) {
    totalErrorInfo = { gain: { ppm: {} }, offset: { ppm: {} } };

    totalErrorInfo.gain.ppm.rss = sumBigDetailsSquared(chartInfo.pieData.find((item) => item.isGain).details).sqrt();
    totalErrorInfo.offset.ppm.rss = sumBigDetailsSquared(
      chartInfo.pieData.find((item) => item.isOffset).details,
    ).sqrt();

    totalErrorInfo.gain.ppm.worstcase = sumBigDetails(chartInfo.pieData.find((item) => item.isGain).details);
    totalErrorInfo.offset.ppm.worstcase = sumBigDetails(chartInfo.pieData.find((item) => item.isOffset).details);
  } else {
    if (!ideal) {
      console.error('Ideal output voltage is required! Setting to 1 to prevent further errors.');
      ideal = 1;
    }

    totalErrorInfo = {
      ideal: ideal,
      ppm: {},
      v: {},
      fs: {},
    };

    totalErrorInfo.ppm.worstcase = chartInfo.pieData.reduce(
      (total, current) => total.add(sumBigDetails(current.details)),
      new Big(0),
    );

    totalErrorInfo.ppm.rss = chartInfo.pieData
      .reduce((total, current) => total.add(sumBigDetailsSquared(current.details)), new Big(0))
      .sqrt();

    totalErrorInfo.ppm.worstcaseTooltip = totalErrorInfo.ppm.worstcase;
    totalErrorInfo.ppm.rssTooltip = totalErrorInfo.ppm.rss;

    totalErrorInfo.v.worstcase = totalErrorInfo.ppm.worstcase
      .div(Math.pow(10, 6))
      .mul(parseFloat(totalErrorInfo.ideal));
    totalErrorInfo.v.rss = totalErrorInfo.ppm.rss.div(Math.pow(10, 6)).mul(parseFloat(totalErrorInfo.ideal));

    totalErrorInfo.fs.worstcase = totalErrorInfo.ppm.worstcase.div(Math.pow(10, 4));
    totalErrorInfo.fs.rss = totalErrorInfo.ppm.rss.div(Math.pow(10, 4));

    if (chartInfo.hasCurrentError && model.signalChainHandler.data.outputRange) {
      let outputRange = model.signalChainHandler.data.outputRange,
        totalRangeValue = outputRange.absoluteSum;

      totalErrorInfo.current = {};
      totalErrorInfo.current.value = outputRange.range[outputRange.range.length - 1];

      //Error (ppm) * |Output Range(3899)| (mA) / 10^6
      if (Utils.isNumeric(totalRangeValue)) {
        totalErrorInfo.current.worstcase = totalErrorInfo.ppm.worstcase.times(totalRangeValue).div(Math.pow(10, 6));
        totalErrorInfo.current.rss = totalErrorInfo.ppm.rss.times(totalRangeValue).div(Math.pow(10, 6));
      }
    }

    // round total error worst case and root-sum-square
    totalErrorInfo.ppm.worstcase = Math.round(totalErrorInfo.ppm.worstcase);
    totalErrorInfo.ppm.rss = Math.round(totalErrorInfo.ppm.rss);
  }

  return totalErrorInfo;
}

export function getDacErrors(dacSelected, dacOutputVoltage, model) {
  // DAC inputs
  const dacInl = tryParseFloat(dacSelected, PARAM.DAC_INL),
    resolution = tryParseFloat(dacSelected, PARAM.RES),
    gainErrorFsTyp = tryParseFloat(dacSelected, PARAM.FS_ERROR),
    gainErrorFsMax = tryParseFloat(dacSelected, PARAM.GAIN_ERROR_PERCENT_FS_MAX),
    gainTempCo = tryParseFloat(dacSelected, PARAM.GAIN_TC_TYP), // 5066             Gain TC
    offsetTempCo = tryParseFloat(dacSelected, PARAM.OFFSET_TEMP_CO), // 4089             Vos TC
    offsetVoltageErrorV = tryParseFloat(dacSelected, PARAM.OFFSET_VOLTAGE_ERROR_V);

  // let vOffset = tryParseFloat(dacSelected,  VOFFSET);
  // let vOut = tryParseFloat(dacSelected,  VOUT);
  // let noiseDensity = tryParseFloat(dacSelected, 4101); // Output Noise? Still no value
  const offsetError = tryParseFloat(dacSelected, PARAM.OFFSET_ERROR),
    // let tempCoefficient = tryParseFloat(dacSelected, 4113)
    maxTempRange = getMaxTempRange(model);

  let dacSelectedErrors = [],
    addDacDetails = addDetailsForErrorCategory(dacSelectedErrors, model);

  const totalUnadjustedError = tryParseFloat(dacSelected, PARAM.TOTAL_UNADJUSTED_ERROR); // TUE

  if (totalUnadjustedError) {
    // TUE error
    addDacDetails(
      'tue',
      true, // always show
      totalUnadjustedError * Math.pow(10, 4),
      notAvailable(['tue']), // no error message to supply
      true, // show help topic
    );
  } else {
    // INL error
    addDacDetails(
      'inl',
      [dacInl, resolution],
      (dacInl / Math.pow(2, resolution)) * Math.pow(10, 6),
      notAvailable(['dac_inl', 'resolution']),
      true, // show help topic
    );

    // Gain Error
    addDacDetails(
      'gain',
      notNull(gainErrorFsMax) || notNull(gainErrorFsTyp), // allow either Max or Typ; prefer Max
      (gainErrorFsMax || gainErrorFsTyp) * Math.pow(10, 4),
      notAvailable(['gain_fs']),
      true, // show help topic
    );

    // Gain Temperature Coefficient (todo: needs formula)
    addDacDetails(
      'gain_tempco',
      [gainTempCo, maxTempRange], // always fails, like Resistor TempCo error
      gainTempCo * maxTempRange,
      notAvailable(!gainTempCo ? ['gain_tempco'] : ['max_temp_range']),
      true, // show help topic
      { gain: true },
    );

    // Offset Error
    addDacDetails(
      'offset',
      notNull(offsetError) || (notNull(dacOutputVoltage, offsetVoltageErrorV) && dacOutputVoltage != 0),
      // use offset error,otherwise try using offset voltage error
      offsetError != null ? offsetError : Math.abs(offsetVoltageErrorV / dacOutputVoltage) * Math.pow(10, 6),
      notAvailable(['offset_ppm', 'dac_out_v', 'offset_v']),
      //'Either ' + notAvailable('Offset Error (PPM)') + ',' + notAvailable('DAC Output Voltage or Offset Voltage Error (V)') + ', or DAC Output Voltage was 0.',
      true, // show help topic
    );

    // Offset Temperature Coefficient
    addDacDetails(
      'offset_tempco',
      [offsetTempCo, dacOutputVoltage],
      // Offset Tempco Error = Offset Tempco (ppm/C) * Maximum delta Temperature
      Math.abs(offsetTempCo * maxTempRange),
      notAvailable(!offsetTempCo ? ['offset_tempco'] : ['dac_out_v']),
      true, // show help topic
      { byPart: true, offset: true },
    );
  }

  return dacSelectedErrors;
}

export function reshuffleDetails(arr) {
  let arr2 = [];
  arr.sort((a, b) => a.value - b.value).reverse();
  for (let i = 0; i < arr.length / 2; i++) {
    arr2.push(arr[i]);
    if (i !== arr.length - i - 1) {
      arr2.push(arr[arr.length - i - 1]);
    }
  }

  // arr2.unshift(arr2.pop()); // rotate elements one to the right
  arr2.push(arr2.shift()); // rotate elements on to the left
  return arr2;
}
